import { useState, useEffect, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '@/stores/hooks';
import { ConflictResolutionDialog } from './conflict-resolution-dialog';
import { VaultDeletedDialog } from './vault-deleted-dialog';
import { resolveConflictThunk, ConflictResolution } from '@/stores/thunks/resolve-conflict-thunk';
import { AlertIcon } from './icons/alert-icon';
import { AlertTriangle } from 'lucide-react';
import { useAllSyncStates } from '@/hooks/use-all-sync-states';
import type { SyncState, ErrorState } from '@/services/sync-state';

/** Active (in-flight) machine statuses */
const ACTIVE_STATUSES = new Set([
  'registering',
  'checking',
  'fetching',
  'uploading',
  'unregistering',
] as const);

/**
 * Maps each active machine state to a target progress percentage (0–90).
 * The bar sweeps smoothly through these values via CSS width transitions as
 * operations progress.  The final 90–100% jump is reserved for the green
 * success flash once the machine returns to idle.
 */
const STATE_PROGRESS: Partial<Record<string, number>> = {
  registering:   20,
  checking:      35,
  fetching:      55,
  uploading:     75,
  unregistering: 60,
};

function getSyncProgress(allStates: Map<string, SyncState>): number {
  for (const state of allStates.values()) {
    const p = STATE_PROGRESS[state.status];
    if (p !== undefined) return p;
  }
  return 40; // fallback if state is active but unrecognised
}

/**
 * Progress bar component for vault sync status.
 * Reads from SyncStateMachine instances (via useAllSyncStates) for live
 * per-vault state.  Falls back to Redux vault-slice only for the
 * vault-deleted notification (no machine state for that yet).
 *
 * Bar colours and animation:
 * - Offline / paused:  yellow  (100%, static)
 * - Active / syncing:  blue    (sweeps 20→90% as machine states advance)
 * - Success:           green   (jumps to 100%, then fades out after 1 s)
 * - Conflict / merging: orange (100%, static)
 * - Error:             red     (100%, drains to 0% over 1 s, then hides)
 * - Deleted (bridge):  purple  (100%, static)
 */
export function SyncProgressBar() {
  const dispatch = useAppDispatch();
  const allStates = useAllSyncStates();

  // Read deleted notification from vault-slice (bridged from sync-orchestrator
  // via vaultDeleted action; cleared by clearVaultDeleted in VaultDeletedDialog).
  const deleted = useAppSelector((state) => state.vault.deleted);

  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [showDeletedDialog, setShowDeletedDialog] = useState(false);
  const [conflictResolution, setConflictResolution] = useState<ConflictResolution | null>(null);

  // --- Derive effective display state from machine states, by priority ------

  const entries = Array.from(allStates.entries());

  const mergingEntry = entries.find(([, s]) => s.status === 'merging');
  const errorEntry   = entries.find(([, s]) => s.status === 'error');
  const activeEntry  = entries.find(([, s]) => ACTIVE_STATUSES.has(s.status as never));
  const pausedEntry  = entries.find(([, s]) => s.status === 'paused');

  type EffectiveStatus = 'conflict' | 'error' | 'syncing' | 'offline' | 'deleted' | 'idle';

  let effectiveStatus: EffectiveStatus = 'idle';
  let conflictVaultId: string | null = null;
  let errorMessage: string | null = null;

  if (mergingEntry) {
    effectiveStatus = 'conflict';
    conflictVaultId = mergingEntry[0];
  } else if (errorEntry) {
    effectiveStatus = 'error';
    errorMessage = (errorEntry[1] as ErrorState).message;
  } else if (activeEntry) {
    effectiveStatus = 'syncing';
  } else if (pausedEntry) {
    effectiveStatus = 'offline';
  } else if (deleted) {
    effectiveStatus = 'deleted';
  }

  // --- Animated bar state ---------------------------------------------------
  // barDisplay drives visibility and colour; barWidth drives the CSS width.
  // CSS transitions on the <div> style handle all smooth movement.

  type BarDisplay = 'hidden' | 'syncing' | 'success' | 'error' | 'offline' | 'conflict' | 'deleted';

  const [barDisplay, setBarDisplay] = useState<BarDisplay>('hidden');
  const [barWidth, setBarWidth]     = useState(0);

  const prevEffectiveRef = useRef<EffectiveStatus>('idle');
  const timerRef         = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const prev = prevEffectiveRef.current;
    prevEffectiveRef.current = effectiveStatus;

    clearTimeout(timerRef.current);

    if (effectiveStatus === 'syncing') {
      // Advance the bar to the progress target for the current machine state
      setBarDisplay('syncing');
      setBarWidth(getSyncProgress(allStates));

    } else if (prev === 'syncing' && effectiveStatus === 'idle') {
      // Sync just completed — sweep to 100% green, then hide after 1 s
      setBarDisplay('success');
      setBarWidth(100);
      timerRef.current = setTimeout(() => {
        setBarDisplay('hidden');
        setBarWidth(0);
      }, 1000);

    } else if (effectiveStatus === 'error') {
      // Show full red bar, then drain to 0% over 1 s and hide
      setBarDisplay('error');
      setBarWidth(100);
      timerRef.current = setTimeout(() => {
        setBarWidth(0);
        timerRef.current = setTimeout(() => setBarDisplay('hidden'), 1100);
      }, 2500);

    } else if (effectiveStatus === 'offline') {
      setBarDisplay('offline');
      setBarWidth(100);

    } else if (effectiveStatus === 'conflict') {
      setBarDisplay('conflict');
      setBarWidth(100);

    } else if (effectiveStatus === 'deleted') {
      setBarDisplay('deleted');
      setBarWidth(100);

    } else {
      // effectiveStatus === 'idle' with no prior syncing — hide immediately
      setBarDisplay('hidden');
      setBarWidth(0);
    }

    return () => clearTimeout(timerRef.current);
  }, [effectiveStatus, allStates]);

  if (barDisplay === 'hidden') {
    return null;
  }

  // --- Colour + transition --------------------------------------------------

  const colorClass: Record<BarDisplay, string> = {
    hidden:   'bg-gray-500',
    syncing:  'bg-blue-500',
    success:  'bg-green-500',
    offline:  'bg-yellow-500',
    conflict: 'bg-orange-500',
    error:    'bg-red-500',
    deleted:  'bg-purple-500',
  };

  // Error drains slowly; everything else animates at 0.3 s
  const transition =
    barDisplay === 'error'
      ? 'width 1s ease-out'
      : 'width 0.3s ease-out';

  // --- Handlers -------------------------------------------------------------

  const handleConflictClick = async () => {
    if (!conflictVaultId) return;
    try {
      const result = await dispatch(
        resolveConflictThunk({ vaultId: conflictVaultId })
      ).unwrap();
      setConflictResolution(result);
      setShowConflictDialog(true);
    } catch (err) {
      console.error('[SyncProgressBar] Failed to fetch conflict details:', err);
    }
  };

  const handleDeletedClick = () => {
    if (!deleted) return;
    setShowDeletedDialog(true);
  };

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 h-1">
        <div
          // eslint-disable-next-line security/detect-object-injection
          className={`h-full ${colorClass[barDisplay]}`}
          style={{ width: `${barWidth}%`, transition }}
        />
        {errorMessage && (
          <div className="absolute top-1 right-2 text-xs text-red-500 bg-white dark:bg-gray-800 px-2 py-1 rounded shadow-sm">
            {errorMessage}
          </div>
        )}
        {effectiveStatus === 'conflict' && (
          <button
            onClick={handleConflictClick}
            className="absolute top-1 right-2 text-xs text-orange-600 bg-white dark:bg-gray-800 px-2 py-1 rounded shadow-sm flex items-center gap-1 hover:bg-orange-50 dark:hover:bg-gray-700 transition-colors"
          >
            <AlertIcon className="h-3 w-3" />
            Sync Conflict - Click to resolve
          </button>
        )}
        {deleted && (
          <button
            onClick={handleDeletedClick}
            className="absolute top-1 right-2 text-xs text-purple-600 bg-white dark:bg-gray-800 px-2 py-1 rounded shadow-sm flex items-center gap-1 hover:bg-purple-50 dark:hover:bg-gray-700 transition-colors"
          >
            <AlertTriangle className="h-3 w-3" />
            Vault Deleted on Server - Click to resolve
          </button>
        )}
      </div>

      <ConflictResolutionDialog
        open={showConflictDialog}
        onOpenChange={setShowConflictDialog}
        conflictResolution={conflictResolution}
      />

      <VaultDeletedDialog
        open={showDeletedDialog}
        onOpenChange={setShowDeletedDialog}
        deletedInfo={deleted}
      />
    </>
  );
}
